//@flow
import React, {useState, useEffect} from 'react';
import {makeStyles, Typography, Tooltip} from '@material-ui/core';
import {colors} from '../../../../assets/styles/colors';
import CustomTextField from '../../../../elements/CustomTextField/CustomTextField';
import {InfoOutlined} from '@material-ui/icons';
import CropModal from '../../../../elements/CropModal';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    width: '100%',
    height: '100%',
  },
  root: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    width: '100%',
    height: '100%',
    borderRadius: '.75rem',
    border: `1px dashed ${colors.grey400}`,
    position: 'relative',
    padding: '1rem',
    background: colors.grey100,

    '&[data-error="true"]': {
      border: `1px dashed ${colors.error}`,
      background: `${colors.error}10`,
    },
  },
  preview: {
    width: '100%',
    height: '100%',
    borderRadius: '.75rem',
    overflow: 'hidden',
    position: 'absolute',

    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
  },

  labelContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: '.5rem',
  },
  label: {
    color: colors.grey600,
    fontSize: '14px',
    fontWeight: 500,
  },

  altInputContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '.5rem',
    width: '100%',
  },
  altInputLabel: {
    fontSize: '14px',
    color: colors.grey600,
    display: 'flex',
    alignItems: 'center',
    gap: '.5rem',
  },

  tooltip: {
    marginLeft: theme.spacing(2),
    backgroundColor: '#F4F4F6',
    borderRadius: '8px',
    textAlign: 'center',
  },
  textTooltip: {
    textAlign: 'left!important',
    color: ' #717190',
    fontSize: '10px',
    '& span': {
      marginLeft: theme.spacing(1),
    },
  },
  infoBtn: {
    color: '#9595AB',
  },
  captionText: {
    fontSize: '0.75rem',
    color: colors.grey600,
    fontWeight: 500,
  },
}));
export type InputImage = {
  image?: string | File,
  alt?: string,
  preview?: string,
};

type Props = {
  altInputLabelAsPlaceholder?: boolean,
  error?: boolean,
  helperText?: string,
  defaultAltText?: string,
  onChange: (image: InputImage) => void,
  defaultImage?: string,
  label?: string,
  aspectRatio: number,
};

export default function ImageInput(props: Props) {
  const {
    altInputLabelAsPlaceholder,
    defaultAltText,
    onChange,
    defaultImage,
    helperText,
    error,
    label = 'Sube una imagen',
    aspectRatio,
  } = props;

  const classes = useStyles();

  const [file, setFile] = useState<File | typeof undefined>();
  const [altText, setAltText] = useState(defaultAltText || '');
  const [fileCropped, setFileCropped] = useState<File | typeof undefined>();

  useEffect(() => {
    if (defaultAltText) {
      setAltText(defaultAltText);
    }
  }, [defaultAltText]);

  const handleFileChange = (event: SyntheticInputEvent<HTMLInputElement>) => {
    const target = event.target;
    const file = target.files[0];

    if (file) {
      setFile(file);
    }
  };

  const inputAltPlaceholder = altInputLabelAsPlaceholder
    ? 'Alt Text: Coloca una descripción de la imágen'
    : 'Coloca una descripción de la imagen';

  return (
    <>
      <div className={classes.container}>
        <Typography className={classes.captionText}>
          Máx. 1 MB por imagen. Archivos más grandes se comprimirán.
        </Typography>
        <label
          role="button"
          className={classes.root}
          data-error={!!error}
          style={{
            aspectRatio: `${aspectRatio}`,
          }}
        >
          <input
            type="file"
            accept="image/*"
            hidden
            onChange={handleFileChange}
          />
          {fileCropped || defaultImage ? (
            <picture className={classes.preview}>
              <img
                src={
                  fileCropped ? URL.createObjectURL(fileCropped) : defaultImage
                }
                alt=""
              />
            </picture>
          ) : (
            <div className={classes.labelContainer}>
              <svg
                width="41"
                height="40"
                viewBox="0 0 41 40"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="20.5" cy="20" r="20" fill="#E8E8ED" />
                <g clipPath="url(#clip0_530_7669)">
                  <path
                    d="M25.4998 26.6663H13.8332V14.9997H21.3332V13.333H13.8332C12.9165 13.333 12.1665 14.083 12.1665 14.9997V26.6663C12.1665 27.583 12.9165 28.333 13.8332 28.333H25.4998C26.4165 28.333 27.1665 27.583 27.1665 26.6663V19.1663H25.4998V26.6663ZM19.0082 24.0247L17.3748 22.058L15.0832 24.9997H24.2498L21.2998 21.0747L19.0082 24.0247ZM27.1665 13.333V10.833H25.4998V13.333H22.9998C23.0082 13.3413 22.9998 14.9997 22.9998 14.9997H25.4998V17.4913C25.5082 17.4997 27.1665 17.4913 27.1665 17.4913V14.9997H29.6665V13.333H27.1665Z"
                    fill="#717190"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_530_7669">
                    <rect
                      width="20"
                      height="20"
                      fill="white"
                      transform="translate(10.5 10)"
                    />
                  </clipPath>
                </defs>
              </svg>

              <span className={classes.label}>{label}</span>
            </div>
          )}
        </label>

        {helperText && (
          <Typography
            color={error ? 'error' : 'textSecondary'}
            variant="caption"
          >
            {helperText}
          </Typography>
        )}
        <div className={classes.altInputContainer}>
          {!altInputLabelAsPlaceholder && (
            <Typography className={classes.altInputLabel}>
              <span>Alt Text (Opcional - Recomendable)</span>
              <AltInputTooltip />
            </Typography>
          )}
          <CustomTextField
            placeholder={inputAltPlaceholder}
            fullWidth
            value={altText}
            helperText="Este texto NO se mostrará como contenido del blog"
            onChange={event => {
              setAltText(event.target.value);
              onChange({image: fileCropped, alt: event.target.value});
            }}
            InputProps={{
              endAdornment: altInputLabelAsPlaceholder && <AltInputTooltip />,
            }}
          />
        </div>
      </div>
      {file && (
        <CropModal
          open={!!file}
          aspect={aspectRatio}
          compressOptions={{
            quality: 1,
            maxWidth: 900,
            maxHeight: 900 / aspectRatio,
          }}
          onClose={() => {
            setFile(undefined);
          }}
          onSubmit={results => {
            const [result] = results;

            const {file} = result;
            setFileCropped(file);

            onChange({image: file, alt: altText});
            setFile(undefined);
          }}
          images={[
            {
              file: file,
              preview: URL.createObjectURL(file),
            },
          ]}
          keepFormat
        />
      )}
    </>
  );
}

function AltInputTooltip() {
  const classes = useStyles();

  return (
    <Tooltip
      placement="right"
      enterTouchDelay={0}
      classes={{tooltip: classes.tooltip}}
      title={
        <Typography className={classes.textTooltip}>
          El alt text o "texto alternativo" es un elemento SEO que describe la
          imagen para buscadores y accesibilidad. Este texto NO se mostrará como
          contenido del blog, solo se usa como atributo alt de la imagen.
        </Typography>
      }
    >
      <InfoOutlined color="primary" className={classes.infoBtn} />
    </Tooltip>
  );
}
